/* oxlint-disable @typescript-eslint/no-shadow */

'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { earnStatisticsBunja, getEarnHistoryOfCurrency } from '@flipster/features/src/shared/earn/state';
import { FormattedMessage } from '@flipster/i18n';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { center, grid, hstack, stack } from '@flipster/styled-system/patterns';
import { PusdtCurrency, UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';

import { Chart } from './chart';

interface Props {
  label: string;
  currency?: string;
}
export const EarnHistory = ({ label, currency }: Props) => {
  const { earnStatisticsAtom } = useBunja(earnStatisticsBunja);
  const earnStatistics = useAtomValue(earnStatisticsAtom);

  const earnStatisticOfCurrency = useMemo(
    () => earnStatistics?.currencies.find((c) => c.currency === currency),
    [earnStatistics, currency],
  );
  const earnHistoryOfCurrency = getEarnHistoryOfCurrency(currency, earnStatistics);

  const cumulativeEarningsEarnProgram = Num(earnStatistics?.cumulativeEarningsEarnProgram ?? 0);
  const cumulativeEarningsEarnPool = Num(earnStatistics?.cumulativeEarningsEarnPool ?? 0);
  const cumulativeEarningsOfAllCurrencies = cumulativeEarningsEarnProgram.plus(cumulativeEarningsEarnPool);

  const cumulativeEarningsOfCurrency = earnStatisticOfCurrency?.cumulativeEarnings ?? 0;
  const isPusdt = currency === PusdtCurrency;
  const payoutCurrency = isPusdt ? UsdtCurrency : (earnStatisticOfCurrency?.payoutCurrency ?? UsdtCurrency);

  return (
    <div className={stack()}>
      <div className={hstack({ justify: 'space-between' })}>
        <div className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-5' })}>
          <FormattedMessage id='Autogen_common.r251126.s_Cumulative' />
        </div>
        <Formatted.Number
          value={currency ? cumulativeEarningsOfCurrency : cumulativeEarningsOfAllCurrencies}
          css={{ textStyle: 'Title/XS', color: 'content.default.level-5' }}
          precision={UsdtPrecision}
          suffix={
            <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-5' })}>
              {payoutCurrency}
            </span>
          }
          toFixed
        />
      </div>
      <dl
        className={grid({
          columns: 2,
          px: { base: 12, tablet: 16 },
          py: 12,
          mt: 8,
          mb: 14,
          bg: 'background.default.level-0',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'border.default.level-2',
          rounded: 8,
          gap: 8,
          color: 'content.default.level-5',
          '& dd': { textAlign: 'right' },
        })}
      >
        <dt>
          <FormattedMessage id='Autogen_common.r251126.s_Today' />
        </dt>
        <dd>
          <Formatted.Number
            value={earnHistoryOfCurrency.at(-1)?.dailyPayoutAmount ?? 0}
            precision={UsdtPrecision}
            withColor
            withPlus
            suffix={<span className={css({ color: 'content.default.level-2' })}>{payoutCurrency}</span>}
            toFixed
          />
        </dd>
        <dt>
          <FormattedMessage id='Autogen_common.r251126.s_Last_7_days' />
        </dt>
        <dd>
          <Formatted.Number
            value={earnHistoryOfCurrency.reduce((acc, curr) => acc.plus(curr.dailyPayoutAmount), Num(0))}
            precision={UsdtPrecision}
            withColor
            withPlus
            suffix={<span className={css({ color: 'content.default.level-2' })}>{payoutCurrency}</span>}
            toFixed
          />
        </dd>
      </dl>
      <div
        className={css({
          px: 20,
          pt: 13,
          pb: 20,
          rounded: 8,
          border: '1px solid {colors.border.default.level-2}',
        })}
      >
        <div className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2', textAlign: 'right' })}>
          <FormattedMessage
            id='Autogen_common.r251126.s_label_weekly'
            values={{
              label: label,
            }}
          />
        </div>
        <div className={center()}>
          <Chart data={earnHistoryOfCurrency} />
        </div>
      </div>
    </div>
  );
};
